import * as React from 'react';
import Tabs from '@mui/material/Tabs';
import Tab from '@mui/material/Tab';
import { Box, Button, FormControl, Grid, InputLabel, MenuItem, Select, Stack, ToggleButton, ToggleButtonGroup, Typography, useMediaQuery } from '@mui/material';
import { useSearchParams } from 'react-router-dom';
import { UserDetailsContext } from '../../../../context/user-details/user-details';
import { ReportPeriodContext } from '../../../../context/report-periods/report-periods';
import gaiaTheme from '../../../../root-components/theme';
import { PageOuterContainer } from '../../../../root-components/layouts/page-outer-container';
import { PageHeading } from '../../../presentation-components/display-elements-components/page-heading';
import { a11yProps } from '../../../../shared/utils/misc-shared-utils';
import { CustomTabPanel } from '../../../presentation-components/display-elements-components/custom-tab-panel';
import { AnalyticsOverview } from '../../../presentation-components/analytics-components/tabs/analytics-overview';
import { AnalyticsByLocation } from '../../../presentation-components/analytics-components/tabs/analytics-by-location';
import { AnalyticsBySubsidiary } from '../../../presentation-components/analytics-components/tabs/analytics-by-subsidiary';
import { AnalyticsByProject } from '../../../presentation-components/analytics-components/tabs/analytics-by-project';
import { AnalyticsByGroup } from '../../../presentation-components/analytics-components/tabs/analytics-by-group';
import { AnalyticsBySupplier } from '../../../presentation-components/analytics-components/tabs/analytics-by-supplier';
import { AnalyticsByEnergy } from '../../../presentation-components/analytics-components/tabs/analytics-by-energy';
import { AnalyticsFlagTab } from '../../../presentation-components/analytics-components/tabs/analytics-flag-tab';
import { AnalyticsSurveysTab } from '../../../presentation-components/analytics-components/tabs/analytics-surveys-tab';
import { ScreenLabelsContext } from '../../../../context/screen-labels/screen-labels';
import {
  analyticsEmissionTypeToggleFromDefaultMarketBased,
  type AnalyticsEmissionTypeToggle,
} from '../../../../shared/utils/default-market-based-utils';
import { useFlagEmissionsCapability } from '../../../../hooks/use-flag-emissions-capability';
import {
  ANALYTICS_EMISSIONS_DIMENSIONS,
  parseAnalyticsViewParams,
  serialiseAnalyticsViewParams,
  type AnalyticsEmissionsDimension,
  type AnalyticsTabKey,
} from '../../../../shared/utils/analytics-view-utils';
import { getPreviousReportPeriod } from '../../../../shared/utils/analytics-utils';
import { EmissionBasisToggle } from '../../../presentation-components/analytics-components/emission-basis-toggle';
import { ComparisonPeriodSelect } from '../../../presentation-components/analytics-components/comparison-period-select';
import AiCopilotService from '../../../../shared/services/ai-copilot/ai-copilot.service';
import AnalyticsService from '../../../../shared/services/analytics/analytics.service';
import { IChatTurn } from '../../../../shared/services/ai-copilot/ai-copilot.model';
import { CopilotPanel } from '../../../copilot/CopilotPanel';
import { AutoAwesomeOutlined } from '@mui/icons-material';

// `homeTabKey` renders this page inline as the user's (or organisation's) home dashboard: the view
// is seeded from the saved analytics tab instead of the URL, and the URL sync is switched off so the
// home address stays /dashboard. Used by App's HomeDashboardRoute.
export const AnalyticsPage = ({ homeTabKey }: { homeTabKey?: string } = {}) => {
  const embedded = !!homeTabKey;
  const [searchParams, setSearchParams] = useSearchParams()
  //Context
  const screenLabelsContext = React.useContext(ScreenLabelsContext)
  const userDetailsContext = React.useContext(UserDetailsContext)
  const userDetails = userDetailsContext.userDetails
  const organisationDefaultMarketBased = userDetails?.organisation?.defaultMarketBased
  const reportPeriodContext = React.useContext(ReportPeriodContext);
  const aiCopilotService = React.useMemo(() => new AiCopilotService(), []);
  const analyticsService = React.useMemo(() => new AnalyticsService(), []);
  const { flagEnabled } = useFlagEmissionsCapability();
  const hasSubsidiaries = userDetails?.organisation.enableSubsidiaries;
  const hasProjects = userDetails?.organisation.enableProjects;
  // The report period comes from the global selector in the top bar; this page no longer carries
  // one of its own, so the two can never disagree.
  const allReportPeriods = reportPeriodContext.allReportPeriods;
  const selectedReportPeriodId = reportPeriodContext.lastSelectedReportId || '';
  //State
  // Seed the view from the URL synchronously so the first render never has a state/URL mismatch.
  // If the serialiser's mount run fires from a state snapshot that disagrees with the URL, it
  // rewrites the URL from stale defaults and the hydrate and serialise effects then correct each
  // other forever ("Maximum update depth exceeded"). Legacy links that need org feature flags to
  // resolve are re-parsed by the hydrate effect once userDetails arrives; the serialiser stays
  // gated until then so the legacy params survive long enough to be resolved.
  const [initialView] = React.useState(() =>
    parseAnalyticsViewParams(
      embedded ? new URLSearchParams({ tab: homeTabKey as string }) : searchParams,
      { hasSubsidiaries, hasProjects, flagEnabled },
    ),
  );
  const [activeTab, setActiveTab] = React.useState<AnalyticsTabKey>(initialView.tab ?? 'overview');
  const [dimension, setDimension] = React.useState<AnalyticsEmissionsDimension>(initialView.dimension ?? 'group');
  const [selectedEmissionType, setSelectedEmissionType] = React.useState<AnalyticsEmissionTypeToggle>(() =>
    initialView.basis ?? analyticsEmissionTypeToggleFromDefaultMarketBased(organisationDefaultMarketBased),
  );
  // '' = automatic (the previous period, derived as it always was); otherwise a report period id.
  const [compareReportPeriodId, setCompareReportPeriodId] = React.useState<string>(initialView.compare ?? '');
  const [copilotAction, setCopilotAction] = React.useState<'movement' | 'summary' | 'dataQuality' | null>(null);
  // The Surveys tab's survey filter lives in the shared toolbar row; the tab reports its
  // options up after each load and the page owns the selection.
  const [surveyOptions, setSurveyOptions] = React.useState<Array<{ id: string; name: string }>>([]);
  const [selectedSurveyId, setSelectedSurveyId] = React.useState<string>('');
  const hasHydratedRef = React.useRef(false);

  React.useEffect(() => {
    setSelectedSurveyId('');
  }, [selectedReportPeriodId]);
  //Layout
  const isMobileMode = useMediaQuery(gaiaTheme.breakpoints.down('md'));

  // The organisation default basis applies only while the URL does not dictate one: a shared
  // link's ?basis= must survive the default arriving after mount.
  React.useEffect(() => {
    if (searchParams.get('basis')) return;
    setSelectedEmissionType(analyticsEmissionTypeToggleFromDefaultMarketBased(organisationDefaultMarketBased))
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [organisationDefaultMarketBased])

  // Hydrate the view from the URL. Keyed on searchParams, not just mount, so in-page navigation to a
  // different view of this page actually switches it. Waits for userDetails because resolving legacy
  // links (?tabId=N from the old per-dimension tabs, ?tab=<name> from saved home views, ?view=flag)
  // needs the org's feature flags. Each field is only written when it differs from current state,
  // which is what stops this and the serialiser below ping-ponging.
  React.useEffect(() => {
    if (!userDetails) return;
    // Home-page mode: the view comes from the saved home tab, not the URL. Resolve it once (org
    // feature flags may only be known now, which is what gates a subsidiary/project/FLAG tab), then
    // leave the user free to switch tabs without it being forced back.
    if (embedded) {
      if (hasHydratedRef.current) return;
      const parsedHome = parseAnalyticsViewParams(new URLSearchParams({ tab: homeTabKey as string }), { hasSubsidiaries, hasProjects, flagEnabled });
      if (parsedHome.tab && parsedHome.tab !== activeTab) setActiveTab(parsedHome.tab);
      if (parsedHome.dimension && parsedHome.dimension !== dimension) setDimension(parsedHome.dimension);
      hasHydratedRef.current = true;
      return;
    }
    const parsed = parseAnalyticsViewParams(searchParams, { hasSubsidiaries, hasProjects, flagEnabled });
    if (parsed.tab && parsed.tab !== activeTab) setActiveTab(parsed.tab);
    if (parsed.dimension && parsed.dimension !== dimension) setDimension(parsed.dimension);
    if (parsed.basis && parsed.basis !== selectedEmissionType) setSelectedEmissionType(parsed.basis);
    // Unlike tab/dim/basis, an absent compare param is meaningful (automatic), so hydration must
    // clear the state too: the back button from ?compare=<id> to a compare-less URL restores
    // automatic. Still written only when different, which keeps the no-ping-pong rule.
    const nextCompare = parsed.compare ?? '';
    if (nextCompare !== compareReportPeriodId) setCompareReportPeriodId(nextCompare);
    hasHydratedRef.current = true;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchParams, userDetails, hasSubsidiaries, hasProjects, flagEnabled]);

  // Serialise the view back to the URL so any analytics view is shareable. replace: true so tab and
  // toggle clicks do not pile up history entries. Rebuilding the params from scratch is also what
  // normalises legacy links to the canonical tab/dim/basis form. Safe on its mount run because the
  // state was seeded from the URL above, so the only mount-time write is canonicalisation.
  React.useEffect(() => {
    // Rendered inline on /dashboard as the home view: never rewrite that URL.
    if (embedded) return;
    if (!hasHydratedRef.current) return;
    const next = serialiseAnalyticsViewParams({
      tab: activeTab,
      dimension,
      basis: selectedEmissionType,
      compare: compareReportPeriodId || undefined,
    });
    if (next.toString() !== searchParams.toString()) setSearchParams(next, { replace: true });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeTab, dimension, selectedEmissionType, compareReportPeriodId]);

  const handleTabChange = (event: React.SyntheticEvent, newValue: AnalyticsTabKey) => {
    setActiveTab(newValue);
  };

  const selectedReportPeriod = allReportPeriods?.find((item) => item.id === selectedReportPeriodId);
  // What automatic resolves to for the OVERVIEW tab (base years suppress it): the overview
  // tiles compare against the previous period by default (product call, 2026-09-15), while
  // every other tab stays opt-in with no comparison until one is chosen.
  const autoComparisonPeriod = selectedReportPeriod?.baseYear
    ? undefined
    : getPreviousReportPeriod(selectedReportPeriodId, allReportPeriods ?? []);
  // An id that no longer resolves (deleted period, or the global selector moved onto the
  // chosen period) counts as no comparison rather than comparing a period with itself.
  const comparisonOverridePeriod = React.useMemo(() => {
    if (!compareReportPeriodId || compareReportPeriodId === selectedReportPeriodId) return undefined;
    return allReportPeriods?.find((item) => item.id === compareReportPeriodId);
  }, [allReportPeriods, compareReportPeriodId, selectedReportPeriodId]);
  // The overview (and its copilot explain action) falls back to the automatic previous
  // period; the chart tabs compare only when a period is explicitly chosen.
  const overviewComparisonPeriod = comparisonOverridePeriod ?? autoComparisonPeriod;
  const comparisonReportPeriod = overviewComparisonPeriod;
  const chartComparisonPeriod = comparisonOverridePeriod
    ? { id: comparisonOverridePeriod.id, label: comparisonOverridePeriod.friendlyName || String(comparisonOverridePeriod.calendarYear ?? 'Comparison period') }
    : undefined;
  const selectedPeriodLabel = selectedReportPeriod?.friendlyName || String(selectedReportPeriod?.calendarYear ?? 'Selected period');
  const canUseCopilot = !!userDetails?.organisation?.aiCopilotEnabled;

  const copilotRequest = () => {
    if (!selectedReportPeriodId) return Promise.reject(new Error('Please select a report period first.'));
    if (copilotAction === 'movement') {
      if (!comparisonReportPeriod?.id) return Promise.reject(new Error('No comparison period is available for this report period.'));
      return aiCopilotService.explainReportMovement({
        reportPeriodId: selectedReportPeriodId,
        comparisonReportPeriodId: comparisonReportPeriod.id,
      });
    }
    if (copilotAction === 'dataQuality') {
      return aiCopilotService.dataQualitySuggestions({ reportPeriodId: selectedReportPeriodId });
    }
    return aiCopilotService.draftReportSection({
      reportPeriodId: selectedReportPeriodId,
      sectionType: 'performance_summary',
    });
  };

  const copilotFollowUpRequest = (userQuestion: string, history?: IChatTurn[]) => {
    if (copilotAction === 'movement' && comparisonReportPeriod?.id) {
      return aiCopilotService.explainReportMovement({
        reportPeriodId: selectedReportPeriodId,
        comparisonReportPeriodId: comparisonReportPeriod.id,
        userQuestion,
        history,
      });
    }
    return aiCopilotService.dataQualitySuggestions({ reportPeriodId: selectedReportPeriodId, userQuestion, history });
  };

  const dimensionLabel = (entry: AnalyticsEmissionsDimension): string => {
    switch (entry) {
      case 'group': return 'Group';
      case 'subsidiary': return screenLabelsContext.getLabel('subsidiary', { plural: false, capitalize: 'first' });
      case 'location': return screenLabelsContext.getLabel('location', { plural: false, capitalize: 'first' });
      case 'supplier': return screenLabelsContext.getLabel('supplier', { plural: false, capitalize: 'first' });
      case 'project': return screenLabelsContext.getLabel('project', { plural: false, capitalize: 'first' });
      default: return entry;
    }
  };

  const availableDimensions = ANALYTICS_EMISSIONS_DIMENSIONS.filter((entry) =>
    (entry !== 'subsidiary' || hasSubsidiaries) && (entry !== 'project' || hasProjects));

  // Record which analytics dashboard view is seen (fire-and-forget telemetry). The page is
  // client-rendered with no single load endpoint, so it pings the server here. Debounced so
  // flicking through tabs only records where the user lands, and de-duplicated so a re-render
  // on the same view does not double count. The Emissions tab reports its active dimension
  // (group, subsidiary, ...) so the keys stay identical to when each dimension was its own tab.
  const lastTrackedTabRef = React.useRef<string | null>(null);
  React.useEffect(() => {
    if (!userDetails) return;
    const tabKey = activeTab === 'emissions' ? dimension : activeTab;
    const timer = setTimeout(() => {
      if (lastTrackedTabRef.current === tabKey) return;
      lastTrackedTabRef.current = tabKey;
      analyticsService.trackDashboardView(tabKey);
    }, 600);
    return () => clearTimeout(timer);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeTab, dimension, userDetails]);


  return (
    <PageOuterContainer>
      <Grid container>
        {/* On the home page (embedded) only the tiles and graphs are shown, so the heading, the AI
            actions, the tab bar and the toolbar (basis / dimension / compare / survey) are all hidden. */}
        {/* The tight tab-bar gap only applies when the tabs sit straight under the heading; the AI
            actions row needs the standard gap. */}
        {!embedded && (
          <PageHeading
            title='Analytics'
            hasTabs={!canUseCopilot}
          />
        )}
        {!embedded && canUseCopilot && (
          <Stack direction="row" flexWrap="wrap" gap={1} sx={{ mb: 2, width: '100%' }}>
            <Button
              variant="outlined"
              startIcon={<AutoAwesomeOutlined fontSize="small" />}
              disabled={!selectedReportPeriodId || !comparisonReportPeriod?.id}
              onClick={() => setCopilotAction('movement')}
            >
              Explain key changes
            </Button>
            <Button
              variant="outlined"
              startIcon={<AutoAwesomeOutlined fontSize="small" />}
              disabled={!selectedReportPeriodId}
              onClick={() => setCopilotAction('summary')}
            >
              Summarise this reporting period
            </Button>
            <Button
              variant="outlined"
              startIcon={<AutoAwesomeOutlined fontSize="small" />}
              disabled={!selectedReportPeriodId}
              onClick={() => setCopilotAction('dataQuality')}
            >
              Suggest data quality improvements
            </Button>
          </Stack>
        )}
        <Box sx={{ width: '100%' }}>
          {!embedded && (
          <Box sx={{ borderBottom: 1, borderColor: 'divider' }}>
            <Tabs
              value={activeTab}
              onChange={handleTabChange}
              variant="scrollable"
              scrollButtons="auto"
              allowScrollButtonsMobile
              sx={{
                minHeight: 48,
                '& .MuiTabs-flexContainer': {
                  columnGap: { xs: 0, sm: 0.5 },
                  rowGap: 0.5,
                },
                '& .MuiTab-root': {
                  minHeight: 48,
                  minWidth: { xs: 'auto', sm: 'auto' },
                  px: { xs: 1.25, sm: 1.75 },
                  py: 1,
                  fontSize: { xs: '0.8rem', sm: '0.875rem' },
                  textTransform: 'none',
                  whiteSpace: 'nowrap',
                },
              }}
            >
              <Tab value='overview' label="Overview" {...a11yProps('overview')} />
              <Tab value='energy' label="Energy" {...a11yProps('energy')} />
              <Tab value='emissions' label="Emissions" {...a11yProps('emissions')} />
              <Tab value='surveys' label="Surveys" {...a11yProps('surveys')} />
              {flagEnabled && <Tab value='flag' label="FLAG" {...a11yProps('flag')} />}
            </Tabs>
          </Box>
          )}
          {!embedded && allReportPeriods.length > 0 && (
            <Box
              sx={{
                mt: 2,
                display: 'flex',
                flexDirection: isMobileMode ? 'column' : 'row',
                flexWrap: 'wrap',
                alignItems: isMobileMode ? 'stretch' : 'center',
                justifyContent: 'flex-start',
                gap: 1.5,
              }}
            >
              {activeTab === 'emissions' && (
                <Stack direction='row' alignItems='center' flexWrap='wrap' gap={1}>
                  <Typography variant='body2' color='text.secondary'>Break down by:</Typography>
                  <ToggleButtonGroup
                    value={dimension}
                    exclusive
                    size='small'
                    onChange={(_e, next: AnalyticsEmissionsDimension | null) => { if (next) setDimension(next); }}
                  >
                    {availableDimensions.map((entry) => (
                      <ToggleButton key={entry} value={entry} sx={{ textTransform: 'none', px: 1.5 }}>
                        {dimensionLabel(entry)}
                      </ToggleButton>
                    ))}
                  </ToggleButtonGroup>
                </Stack>
              )}
              {activeTab === 'surveys' && surveyOptions.length > 0 && (
                <FormControl size='small' sx={{ minWidth: 280 }}>
                  <InputLabel>Survey</InputLabel>
                  <Select
                    value={selectedSurveyId}
                    label='Survey'
                    onChange={(e) => setSelectedSurveyId(e.target.value)}
                  >
                    <MenuItem value=''>All surveys</MenuItem>
                    {surveyOptions.map((s) => (
                      <MenuItem key={s.id} value={s.id}>{s.name}</MenuItem>
                    ))}
                  </Select>
                </FormControl>
              )}
              {/* ml auto keeps this pair on the right whether or not the dimension selector renders. */}
              <Box sx={{ display: 'flex', flexDirection: isMobileMode ? 'column' : 'row', flexWrap: 'wrap', alignItems: isMobileMode ? 'stretch' : 'center', gap: 1.5, ml: isMobileMode ? 0 : 'auto' }}>
                {/* Hidden by the component itself when the org has fewer than two periods. */}
                <ComparisonPeriodSelect
                  value={compareReportPeriodId}
                  onChange={setCompareReportPeriodId}
                  reportPeriods={allReportPeriods}
                  selectedReportPeriodId={selectedReportPeriodId}
                  // The overview compares automatically, so its empty option says what it
                  // does; the chart tabs stay opt-in and say "No comparison".
                  noneOption={activeTab !== 'overview'}
                  autoComparisonPeriod={activeTab === 'overview' ? autoComparisonPeriod : undefined}
                  fullWidth={isMobileMode}
                />
                {/* The Energy tab's kWh widgets have no basis and ignore this; its Scope 1 and
                    Scope 2 by source widgets follow it, as every other tCO2e surface does. */}
                <EmissionBasisToggle
                  value={selectedEmissionType}
                  onChange={setSelectedEmissionType}
                  orientation={isMobileMode ? 'vertical' : 'horizontal'}
                  fullWidth={isMobileMode}
                  // Content-width buttons: the fixed 200px pair pushed the shared toolbar row
                  // past 1500px once the dimension selector joined it, wrapping Compare to.
                  buttonSx={{ pt: 0, pb: 0, px: 2, width: !isMobileMode ? 'auto' : '100%', height: 40 }}
                  sx={{ width: isMobileMode ? '100%' : 'auto' }}
                />
              </Box>
            </Box>
          )}
          <CustomTabPanel value={activeTab} index='overview'>
            <AnalyticsOverview
              reportPeriodId={selectedReportPeriodId}
              emissionType={selectedEmissionType}
              comparisonPeriodOverride={overviewComparisonPeriod} />
          </CustomTabPanel>
          <CustomTabPanel value={activeTab} index='energy'>
            <AnalyticsByEnergy
              reportPeriodId={selectedReportPeriodId}
              comparisonPeriod={chartComparisonPeriod}
              periodLabel={selectedPeriodLabel}
              emissionType={selectedEmissionType}
            />
          </CustomTabPanel>
          <CustomTabPanel value={activeTab} index='emissions'>
            {/* One Emissions tab with a dimension selector, in place of five "Emissions by X"
                tabs. The selector renders in the shared toolbar row above, beside Compare to. */}
            {dimension === 'group' && (
              <AnalyticsByGroup
                reportPeriodId={selectedReportPeriodId}
                emissionType={selectedEmissionType}
                comparisonPeriod={chartComparisonPeriod}
                periodLabel={selectedPeriodLabel}
              />
            )}
            {dimension === 'subsidiary' && hasSubsidiaries && (
              <AnalyticsBySubsidiary
                reportPeriodId={selectedReportPeriodId}
                emissionType={selectedEmissionType}
                comparisonPeriod={chartComparisonPeriod}
                periodLabel={selectedPeriodLabel} />
            )}
            {dimension === 'location' && (
              <AnalyticsByLocation
                reportPeriodId={selectedReportPeriodId}
                emissionType={selectedEmissionType}
                comparisonPeriod={chartComparisonPeriod}
                periodLabel={selectedPeriodLabel} />
            )}
            {dimension === 'supplier' && (
              <AnalyticsBySupplier
                reportPeriodId={selectedReportPeriodId}
                emissionType={selectedEmissionType}
                comparisonPeriod={chartComparisonPeriod}
                periodLabel={selectedPeriodLabel} />
            )}
            {dimension === 'project' && hasProjects && (
              <AnalyticsByProject
                reportPeriodId={selectedReportPeriodId}
                emissionType={selectedEmissionType}
                comparisonPeriod={chartComparisonPeriod}
                periodLabel={selectedPeriodLabel} />
            )}
          </CustomTabPanel>
          <CustomTabPanel value={activeTab} index='surveys'>
            <AnalyticsSurveysTab
              reportPeriodId={selectedReportPeriodId}
              selectedSurveyId={selectedSurveyId}
              onSurveysLoaded={setSurveyOptions}
            />
          </CustomTabPanel>
          {flagEnabled && (
            <CustomTabPanel value={activeTab} index='flag'>
              <AnalyticsFlagTab reportPeriodId={selectedReportPeriodId} emissionType={selectedEmissionType} />
            </CustomTabPanel>
          )}
        </Box>
      </Grid >
      {canUseCopilot && copilotAction && (
        <CopilotPanel
          open={!!copilotAction}
          title={
            copilotAction === 'movement'
              ? 'Explain key changes'
              : copilotAction === 'dataQuality'
                ? 'Data quality suggestions'
                : 'Reporting period summary'
          }
          onClose={() => setCopilotAction(null)}
          request={copilotRequest}
          followUpRequest={copilotAction === 'summary' ? undefined : copilotFollowUpRequest}
          suggestedActions={copilotAction === 'dataQuality' ? [
            { label: 'What should we fix first?', question: 'What should we fix first?' },
            { label: 'Which spend-based records matter most?', question: 'Which spend-based records matter most?' },
          ] : []}
        />
      )}
    </PageOuterContainer>
  )
}
